import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DefaultIcon, SDate } from '@summit/core';

import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  DateBarTypes,
  DateRange,
  DateRangeType,
  FilterParameter,
  IUserExperience,
  SummitIdObject,
  SummitUserExperienceService
} from '@summit/models';
import { LayoutService, SUiTieredMenuComponent } from '@summit/ui';
import { Dayjs } from 'dayjs';
import { MenuItem } from 'primeng/api';
import { Button } from 'primeng/button';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { Popover } from 'primeng/popover';
import { TieredMenu } from 'primeng/tieredmenu';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, distinctUntilChanged, Observable, takeUntil } from 'rxjs';
import { SummitFormBase } from '../../models';
import { SFormInputCheckboxComponent } from '../s-form-input-checkbox/s-form-input-checkbox.component';
import { SFormInputDateComponent } from '../s-form-input-date/s-form-input-date.component';
import { SFormInputMonthRangeComponent } from '../s-form-input-month-range/s-form-input-month-range.component';
import { SFormInputMonthComponent } from '../s-form-input-month/s-form-input-month.component';
import { SFormInputTextComponent } from '../s-form-input-text/s-form-input-text.component';
import { SFormInputWeekRangeComponent } from '../s-form-input-week-range/s-form-input-week-range.component';
import { SFormInputWeekComponent } from '../s-form-input-week/s-form-input-week.component';

@Component({
  selector: 's-form-title-bar',
  templateUrl: './s-form-title-bar.component.html',
  styleUrls: ['./s-form-title-bar.component.scss'],
  standalone: true,
  imports: [
    Button,
    TieredMenu,
    ReactiveFormsModule,
    SFormInputTextComponent,
    SFormInputCheckboxComponent,
    SFormInputWeekRangeComponent,
    SFormInputWeekComponent,
    SFormInputMonthRangeComponent,
    SFormInputMonthComponent,
    SFormInputDateComponent,
    SUiTieredMenuComponent,
    InputGroupAddon,
    NgTemplateOutlet,
    AsyncPipe,
    Tooltip,
    Popover
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormTitleBarComponent extends SummitFormBase implements OnInit {
  @Input() showNewButton = true;
  @Input() newButtonLabel = 'New';
  @Input() newButtonTooltip: string = 'New Entry';
  @Input() dateBarType: DateBarTypes;
  @Input() dateBarLabel?: string;
  @Input() defaultDateRange: DateRange;
  @Input() menuItems$: Observable<MenuItem[]>;
  @Input() showFilter = true;
  @Input() showDownload = true;
  @Input() showPrint = true;
  @Input() dateRangeType: DateRangeType;
  @Input() printSetup: 'grid' | 'browser' = 'grid';
  @Input() updateUrl: boolean;
  override skipUnloadTest: boolean = true;

  @Input() showCustomFilterMenu = false;
  customFilters$: Observable<FilterParameter[] | undefined> | undefined;

  @Output() addEvent = new EventEmitter<any>();
  @Output() refresh = new EventEmitter<any>();
  @Output() filterString = new EventEmitter<string>();
  @Output() serverFilterChange = new EventEmitter<any>();
  @Output() dateRangeChange = new EventEmitter<DateRange>();
  @Output() dateChange = new EventEmitter<Dayjs>();
  @Output() print = new EventEmitter<unknown>();
  @Output() exportCopy = new EventEmitter<any>();
  @Output() exportCsv = new EventEmitter<any>();
  @Output() exportExcel = new EventEmitter<any>();

  @Input() defaultDay = new Date();
  oldDate: Dayjs;
  oldDateRange: DateRange;

  @ViewChild('filterInput', { static: false }) filterInput: SFormInputTextComponent<SummitIdObject>;
  @ViewChild('rangePicker', { static: false }) rangePicker:
    | SFormInputWeekRangeComponent
    | SFormInputMonthRangeComponent;
  @ContentChild('menuSuffix') menuSuffixTemplate: TemplateRef<unknown>;
  @ContentChild('filter') filterTemplate: TemplateRef<unknown>;
  @ContentChild('viewSuffix') viewSuffixTemplate: TemplateRef<unknown>;

  mobileMenuItems: MenuItem[];

  ux$ = this.uxService.currentUserExperience;
  defaultIcon = DefaultIcon;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public layoutService: LayoutService,
    private formBuilder: FormBuilder,
    private uxService: SummitUserExperienceService<IUserExperience>
  ) {
    super();
    this.elementForm = this.formBuilder.group({
      filterString: ['', []]
    });
  }

  ngOnInit() {
    const weekRangeParameter = this.route.snapshot?.queryParams['WeekRange'];
    // console.log(`Got week range from url`, weekRangeParameter);
    if (weekRangeParameter) {
      let dateRange: DateRange;
      //console.log(`Title Bar [Const]: Date Range Type: ${DateRangeTypeMapping[this.dateRangeType]}`);
      try {
        // Rule fix const data = JSON.parse(weekRangeParameter);
        dateRange = new DateRange(JSON.parse(weekRangeParameter).filterData);
      } catch (e) {
        // console.log(`Special String: treating as DateRange Week Type`);
        dateRange = new DateRange({ newWeekType: weekRangeParameter }, this.dateRangeType);
      }
      this.defaultDateRange ??= dateRange;
      // console.log(`Looking at Date Parameters`, dateRange);
    }
    const dateParameter = this.route.snapshot?.queryParams['Date'];
    // console.log(`Got week range from url`, weekRangeParameter);
    if (dateParameter) {
      //console.log(`Looking at Date Parameters ${dateParameter}`);
      this.defaultDay = SDate.new(JSON.parse(dateParameter).filterData).toDate();
    }
    const startingFilterString = this.route.snapshot?.queryParams['filter'];
    // console.log(`Got week range from url`, weekRangeParameter);
    if (startingFilterString) {
      this.elementForm.controls['filterString'].setValue(startingFilterString);
    }

    this.mobileMenuItems = [
      {
        label: 'Copy to Clipboard',
        command: () => {
          this.triggerExportCopy();
        },
        visible: this.showDownload
      },
      {
        label: 'Export to CSV',
        command: () => {
          this.triggerExportCSV();
        },
        visible: this.showDownload
      },
      {
        label: 'Export to Excel',
        command: () => {
          this.triggerExportExcel();
        },
        visible: this.showDownload
      },
      {
        label: 'Print',
        command: () => {
          this.triggerPrint();
        },
        visible: this.showPrint
      }
    ].filter((item) => item.visible);

    this.elementForm.controls['filterString'].valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.disposing$))
      .subscribe(async (value) => {
        //console.log(`Filter String Changed: ${value}`);
        this.filterString.emit(value);
      });
  }

  async changeDate(date: Dayjs) {
    //console.log(`Title Bar: Date changed to ${date?.format()}`, date);
    const lclDate = SDate.newUtc(date?.startOf('day').format('YYYY-MM-DD'));
    if (lclDate?.isSame(this.oldDate)) {
      return;
    }
    this.oldDate = lclDate;
    //should look at the other parts of Date and merge the object
    if (this.updateUrl) {
      //console.log(`Updating URL with Date: ${lclDate.format('YYYY-MM-DD')}`);
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          Date: JSON.stringify({ filterData: lclDate.format('YYYY-MM-DD') })
        },
        queryParamsHandling: 'merge',
        preserveFragment: true
      });
    }
    this.dateChange.emit(lclDate);
  }

  changeDateRange(newDateRange: DateRange) {
    if (newDateRange.begin?.isSame(this.oldDateRange?.begin) && newDateRange.end?.isSame(this.oldDateRange?.end)) {
      return;
    }
    this.oldDateRange = newDateRange;
    //should look at the other parts of WeekRange and merge the object
    if (this.updateUrl) {
      //console.log(`Updating URL with Date`);
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          WeekRange: JSON.stringify(newDateRange)
        },
        queryParamsHandling: 'merge',
        preserveFragment: true,
        skipLocationChange: true
      });
    }
    this.dateRangeChange.emit(newDateRange);
  }

  downloadMenuItems: MenuItem[] = [
    {
      label: 'Copy to Clipboard',
      command: () => {
        this.triggerExportCopy();
      }
    },
    {
      label: 'Export to CSV',
      command: () => {
        this.triggerExportCSV();
      }
    },
    {
      label: 'Export to Excel',
      command: () => {
        this.triggerExportExcel();
      }
    }
  ];

  // Export Functions
  async triggerExportCopy() {
    this.exportCopy.emit();
  }
  async triggerExportCSV() {
    this.exportCsv.emit();
  }

  async triggerExportExcel() {
    this.exportExcel.emit();
  }
  async triggerServerFilterChange() {
    this.serverFilterChange.emit();
  }

  triggerPrint() {
    if (this.printSetup === 'browser') {
      window.print();
    } else {
      this.print.emit();
    }
  }

  public async triggerAdd() {
    // console.log(`add new entry`);
    const params: any = new Object();
    // params.DateRange = this.rangePicker?.getRange();
    this.addEvent.emit(params);
  }
  async triggerRefreshData() {
    this.refresh.emit();
  }

  filterClear() {
    //console.log(`clear event and focus: ${this.filterInput.hasFocus}`);
    if (this.elementForm.controls['filterString'].value && !this.filterInput.hasFocus) {
      this.elementForm.controls['filterString'].setValue(null);
      // this.updateTableView();
    }
  }
}
